<template>
    <div  v-if='modelValue'>
    <div id="aliyun-captcha-container" ref="captchaRef" class="captcha-container"></div>
    </div>
</template>

<script setup>
import { ref, watch, nextTick, onUnmounted } from 'vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  captchaId: {
    type: String,
    required: true
  },
})

const emit = defineEmits(['update:modelValue', 'success', 'error', 'close'])

const captchaRef = ref(null)
const loading = ref(false)
const errorMsg = ref('')
let captchaObj = null

// 检查 SDK 是否加载 - 使用 ct4.js 的方式
const checkSDK = () => {
  return new Promise((resolve, reject) => {
    // 使用的是 initAlicom4
    if (typeof window.initAlicom4 === 'function') {
      resolve()
      return
    }
    
    let attempts = 0
    const maxAttempts = 50
    
    const timer = setInterval(() => {
      attempts++
      if (typeof window.initAlicom4 === 'function') {
        clearInterval(timer)
        resolve()
      } else if (attempts >= maxAttempts) {
        clearInterval(timer)
        reject(new Error('验证码 SDK 加载超时'))
      }
    }, 100)
  })
}

// 初始化验证码 
const initCaptcha = async () => {
  if (!process.client) return
  
  try {
    loading.value = true
    errorMsg.value = ''
    
    // 检查 captchaId
    if (!props.captchaId) {
      throw new Error('缺少 captchaId 参数')
    }
    
    // 等待 SDK 加载
    await checkSDK()
    await nextTick()
    
    // 清理旧实例
    if (captchaObj) {
      try {
        captchaObj.reset()
        captchaObj = null
      } catch (e) {
        console.log('清理旧实例失败:', e)
      }
    }
    
    // 清空容器内容
    const container = document.getElementById('aliyun-captcha-container')
    if (container) {
      container.innerHTML = ''
    }
    
    console.log('初始化阿里云通信验证码:', {
      captchaId: props.captchaId,
      hasContainer: !!document.getElementById('aliyun-captcha-container')
    })
    
    // 使用 initAlicom4
    window.initAlicom4({
      captchaId: props.captchaId,
      product: 'bind',        // 弹出式
      https: true,
      width: props.width,
      renderTo: '#aliyun-captcha-container',  // 使用 ID 选择器
      
      // 初始化失败回调
      onError: (err) => {
        console.error('验证码初始化失败:', err)
        errorMsg.value = err?.msg || '验证码加载失败'
        loading.value = false
        emit('error', { type: 'INIT_ERROR', message: err?.msg })
      }
      
    }, (obj) => {
      // 初始化成功回调 
      console.log('验证码初始化成功:', obj)
       // 关闭弹框
       emit('update:modelValue', false)
      captchaObj = obj
      loading.value = false
      
      // 绑定成功事件
      obj.onSuccess((data) => {
        const validate = data || obj.getValidate()
        console.log('验证成功:', validate)
        
       
        
        // 发送成功事件
        emit('success', {
          validate: validate,
          captchaId: props.captchaId
        })
      })
      
      // 绑定错误事件
      obj.onError((err) => {
        console.error('验证错误:', err)
        emit('error', { 
          type: 'VERIFY_ERROR', 
          message: err?.msg || '验证失败' 
        })
      })
      
      // 显示验证码 
      obj.showCaptcha()
    })
    
  } catch (err) {
    console.error('初始化异常:', err)
    errorMsg.value = err.message || '初始化异常'
    loading.value = false
    emit('error', { type: 'EXCEPTION', message: err.message })
  }
}

// 监听弹框显示 - 关键：使用 watch 监听 modelValue
watch(() => props.modelValue, (newVal) => {
  console.log('弹框状态变化:', newVal, 'captchaId:', props.captchaId)
  if (newVal && process.client) {
    // 延迟确保 DOM 就绪
    setTimeout(() => {
      initCaptcha()
    }, 300)
  }
}, { immediate: false })

// 弹框关闭清理
const handleClosed = () => {
  console.log('弹框关闭，清理资源')
  if (captchaObj) {
    try {
      captchaObj.reset()
    } catch (e) {}
    captchaObj = null
  }
  loading.value = false
  errorMsg.value = ''
  emit('close')
}

onUnmounted(() => {
  handleClosed()
})
</script>

<style scoped>
.captcha-wrapper {
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.loading-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: #606266;
  font-size: 14px;
}

.error-state {
  color: #f56c6c;
}

.error-state .el-icon {
  font-size: 32px;
}

.captcha-container {
  width: 100%;
  display: flex;
  justify-content: center;
  min-height: 40px;
}

:deep(.el-dialog__header) {
  margin-right: 0;
  text-align: center;
  font-weight: 600;
}

:deep(.el-dialog__body) {
  padding: 0;
}
</style>